import { useState } from 'react';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { copyToClipboard, isSafari } from 'service/browser';
import { useShortUrl } from 'hooks/useShortUrl';

import { pageAlertConfigState } from 'components/common/PageAlert/PageAlert.state';
import { absoluteResolvedUrlState } from 'components/common/Managers/RouteStateManager.state';
import { PageAlertVariant } from 'components/common/PageAlert/PageAlert.types';

/**
 * This hook provides functions for copying a short url link to the clipboard
 *
 * @prop urlToShorten     the url that would be shortened and copied
 *
 */
export const useCopyShortLink = (urlToShorten: string) => {
  const { fetchShortUrlPromise } = useShortUrl();
  const absoluteUrl = useRecoilValue(absoluteResolvedUrlState);
  const [ copyLinkIsAvailable, setCopyLinkIsAvailable ] = useState(!isSafari);
  const [ shortUrl, setShortUrl ] = useState<string | null>(null);
  const setPageAlertConfig = useSetRecoilState(pageAlertConfigState);
  const [ previousShortenedUrl,  setPreviousShortenedUrl ] = useState<string>('');

  const showErrorAlert = (message: string): void => {
    setPageAlertConfig({ message, variant: PageAlertVariant.ERROR, autoHide: true });
  };

  const showSuccessAlert = (message: string): void => {
    setPageAlertConfig({ message, variant: PageAlertVariant.SUCCESS, autoHide: true });
  };

  const copyShortLink = (): void => {
    const successHandler = () => showSuccessAlert('Link copied to clipboard');
    const errorHandler = () => showErrorAlert('Could not copy link to clipboard');
    if (isSafari) {
      if (!copyLinkIsAvailable) {
        return;
      }
      if (shortUrl) {
        copyToClipboard(shortUrl, successHandler, errorHandler);
      } else {
        errorHandler();
      }
    } else {
      fetchShortUrlPromise(urlToShorten || absoluteUrl).then(({ data }) => {
        copyToClipboard(data?.compressUrl || '', successHandler, errorHandler);
      });
    }
  };

  const fetchShortUrlForSafari = () => {
    setCopyLinkIsAvailable(false);
    fetchShortUrlPromise(urlToShorten || absoluteUrl).then(({ data }) => {
      setShortUrl(data?.compressUrl || '');
      setPreviousShortenedUrl(urlToShorten);
      setCopyLinkIsAvailable(true);
    });
  };

  return {
    copyShortLink,
    copyLinkIsAvailable,
    setCopyLinkIsAvailable,
    setShortUrl,
    previousShortenedUrl,
    fetchShortUrlForSafari,
  };
};
